import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  format,
  addDays,
  addMonths,
  differenceInDays,
  parseISO,
  isValid,
  eachWeekOfInterval,
  eachMonthOfInterval,
} from 'date-fns';
import { Task, OfficeId } from '../../lib/types';
import { OFFICES, getOffice } from '../../lib/config';
import { apiListTasks, apiUpdateTask } from '../../lib/api';

// ── Types ──────────────────────────────────────────────────────────────────────

interface GanttChartFrappeProps {
  officeId?: OfficeId;
}

type ViewMode = 'Day' | 'Week' | 'Month';

interface DragState {
  taskId: string;
  originalStart: string;
  originalEnd: string;
  startX: number;
  currentDeltaDays: number;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const DAY_PX: Record<ViewMode, number> = {
  Day: 40,
  Week: 28,
  Month: 14,
};

const LABEL_W = 160; // px — left label column

// Window of days to show
const WINDOW: Record<ViewMode, { past: number; total: number }> = {
  Day: { past: 7, total: 60 },
  Week: { past: 14, total: 84 },
  Month: { past: 30, total: 180 },
};

/**
 * GanttChartFrappe — pure Tailwind/React Gantt chart.
 *
 * Features:
 *  - View modes: Day / Week / Month (Ctrl+wheel to zoom)
 *  - Office swimlanes
 *  - Drag bars left/right to reschedule (optimistic save)
 *  - Progress bar overlay per task
 *  - Toast on save / error
 *  - Today line
 *  - No external Gantt library — zero frappe-gantt dependency
 */
export default function GanttChartFrappe({ officeId }: GanttChartFrappeProps) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('Week');
  const [selected, setSelected] = useState<Task | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tasksRef = useRef<Task[]>([]);

  // Keep ref in sync so drag callbacks always see latest task list
  useEffect(() => { tasksRef.current = tasks; }, [tasks]);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // Fetch tasks
  useEffect(() => {
    setLoading(true);
    apiListTasks(officeId)
      .then((all) => setTasks(all.filter((t) => t.start_date || t.end_date)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [officeId]);

  // Ctrl+wheel zoom
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const MODES: ViewMode[] = ['Day', 'Week', 'Month'];
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      setViewMode((cur) => {
        const idx = MODES.indexOf(cur);
        const next = e.deltaY > 0
          ? Math.min(idx + 1, MODES.length - 1)
          : Math.max(idx - 1, 0);
        return MODES[next];
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // ── Derived timeline geometry ──────────────────────────────────────────────
  const dayPx = DAY_PX[viewMode];
  const { past, total } = WINDOW[viewMode];
  const today = new Date();
  const startDate = addDays(today, -past);
  const endDate = addDays(startDate, total);

  // ── Drag handling ──────────────────────────────────────────────────────────

  const onBarMouseDown = useCallback(
    (e: React.MouseEvent, task: Task) => {
      e.preventDefault();
      e.stopPropagation();
      setDrag({
        taskId: task.id,
        originalStart: task.start_date ?? '',
        originalEnd: task.end_date ?? '',
        startX: e.clientX,
        currentDeltaDays: 0,
      });
    },
    [],
  );

  useEffect(() => {
    if (!drag) return;

    const onMove = (e: MouseEvent) => {
      const deltaX = e.clientX - drag.startX;
      const deltaDays = Math.round(deltaX / dayPx);
      setDrag((d) => d ? { ...d, currentDeltaDays: deltaDays } : null);
    };

    const onUp = async () => {
      if (!drag) return;
      const deltaDays = drag.currentDeltaDays;
      setDrag(null);

      if (deltaDays === 0) return; // no actual move

      const task = tasksRef.current.find((t) => t.id === drag.taskId);
      if (!task) return;

      const origStart = task.start_date ? parseISO(task.start_date) : new Date();
      const origEnd = task.end_date ? parseISO(task.end_date) : addDays(origStart, 3);

      const newStart = format(addDays(origStart, deltaDays), 'yyyy-MM-dd');
      const newEnd = format(addDays(origEnd, deltaDays), 'yyyy-MM-dd');

      // Optimistic update
      setTasks((prev) =>
        prev.map((t) =>
          t.id === drag.taskId ? { ...t, start_date: newStart, end_date: newEnd } : t,
        ),
      );
      setUpdatingId(drag.taskId);

      try {
        await apiUpdateTask(drag.taskId, { start_date: newStart, end_date: newEnd });
        setToast({ message: `Rescheduled: ${task.title}`, type: 'success' });
      } catch (err: any) {
        // Roll back
        setTasks((prev) =>
          prev.map((t) =>
            t.id === drag.taskId
              ? { ...t, start_date: drag.originalStart, end_date: drag.originalEnd }
              : t,
          ),
        );
        setToast({
          message: `Reschedule failed: ${err?.response?.data?.detail ?? err.message}`,
          type: 'error',
        });
      } finally {
        setUpdatingId(null);
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [drag, dayPx]);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="h-6 w-6 rounded-full border-2 border-gray-200 border-t-gray-600 animate-spin" />
      </div>
    );
  }

  const offices = officeId ? OFFICES.filter((o) => o.id === officeId) : OFFICES;

  const totalW = LABEL_W + total * dayPx;

  return (
    <div
      ref={containerRef}
      className="flex flex-col h-full overflow-hidden select-none"
      data-testid="gantt-chart-frappe"
    >
      {/* ── Toolbar ── */}
      <div className="px-6 py-3 border-b border-gray-100 shrink-0 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-700">
            {officeId ?? 'All Offices'}
          </span>
          <span className="text-xs text-gray-400">
            ({tasks.length} tasks)
          </span>
        </div>

        {/* View mode pills */}
        <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-0.5">
          {(['Day', 'Week', 'Month'] as ViewMode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setViewMode(m)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                viewMode === m
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {m}
            </button>
          ))}
        </div>

        <p className="text-xs text-gray-400 hidden md:block">
          Drag to reschedule · Ctrl+wheel to zoom
        </p>
      </div>

      {/* ── Chart area ── */}
      <div className="flex-1 overflow-auto" style={{ minHeight: 0 }}>
        <div style={{ width: totalW, minWidth: '100%' }}>

          {/* Date axis */}
          <DateAxis
            viewMode={viewMode}
            startDate={startDate}
            endDate={endDate}
            total={total}
            dayPx={dayPx}
          />

          {/* Swimlanes */}
          <div className="relative">
            {/* Today line */}
            <TodayLine startDate={startDate} total={total} dayPx={dayPx} />

            {offices.map((office) => {
              const officeTasks = tasks.filter((t) => t.office_id === office.id);
              return (
                <div key={office.id} className="border-b border-gray-100">
                  {/* Lane header */}
                  <div className="flex items-center gap-2 px-4 py-1.5 bg-gray-50/70 border-b border-gray-100">
                    <div
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: office.color }}
                    />
                    <span className="text-xs font-semibold text-gray-700">{office.name}</span>
                    <span className="text-[10px] text-gray-400">({officeTasks.length})</span>
                  </div>

                  {/* Task rows */}
                  {officeTasks.length === 0 ? (
                    <EmptyRow total={total} dayPx={dayPx} />
                  ) : (
                    officeTasks.map((task) => (
                      <GanttRow
                        key={task.id}
                        task={task}
                        startDate={startDate}
                        total={total}
                        dayPx={dayPx}
                        color={office.color}
                        isSelected={selected?.id === task.id}
                        isDragging={drag?.taskId === task.id}
                        dragDeltaDays={drag?.taskId === task.id ? drag.currentDeltaDays : 0}
                        isUpdating={updatingId === task.id}
                        onClick={() => setSelected((s) => s?.id === task.id ? null : task)}
                        onBarMouseDown={(e) => onBarMouseDown(e, task)}
                      />
                    ))
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Detail drawer ── */}
      {selected && (
        <TaskDetail task={selected} onClose={() => setSelected(null)} />
      )}

      {/* ── Saving indicator ── */}
      {updatingId && (
        <div className="fixed bottom-16 right-4 px-3 py-1.5 rounded-md bg-gray-800 text-white text-xs shadow-lg z-50">
          Saving…
        </div>
      )}

      {/* ── Toast ── */}
      {toast && (
        <div
          role="alert"
          className={`fixed bottom-4 right-4 px-4 py-3 rounded-xl text-sm font-medium text-white shadow-lg z-50 transition-all ${
            toast.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

// ── Date Axis ─────────────────────────────────────────────────────────────────

function DateAxis({
  viewMode, startDate, endDate, total, dayPx,
}: {
  viewMode: ViewMode;
  startDate: Date;
  endDate: Date;
  total: number;
  dayPx: number;
}) {
  const today = new Date();

  if (viewMode === 'Day') {
    // Show every day
    return (
      <div
        className="flex sticky top-0 z-10 bg-white border-b border-gray-100"
        style={{ width: LABEL_W + total * dayPx }}
      >
        <div className="shrink-0 border-r border-gray-100" style={{ width: LABEL_W }} />
        {Array.from({ length: total }).map((_, i) => {
          const d = addDays(startDate, i);
          const isToday = format(d, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd');
          return (
            <div
              key={i}
              className={`shrink-0 border-r border-gray-50 flex flex-col items-center justify-center py-1.5 ${
                isToday ? 'bg-blue-50' : ''
              }`}
              style={{ width: dayPx }}
            >
              <span className={`text-[9px] font-semibold uppercase ${isToday ? 'text-blue-600' : 'text-gray-400'}`}>
                {format(d, 'EEE')}
              </span>
              <span className={`text-[10px] font-bold ${isToday ? 'text-blue-600' : 'text-gray-600'}`}>
                {format(d, 'd')}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  if (viewMode === 'Week') {
    const weeks = eachWeekOfInterval({ start: startDate, end: endDate }, { weekStartsOn: 1 });
    return (
      <div
        className="flex sticky top-0 z-10 bg-white border-b border-gray-100"
        style={{ width: LABEL_W + total * dayPx }}
      >
        <div className="shrink-0 border-r border-gray-100" style={{ width: LABEL_W }} />
        {weeks.map((weekStart, i) => {
          const offsetDays = differenceInDays(weekStart, startDate);
          const left = offsetDays * dayPx;
          if (left < 0 || left >= total * dayPx) return null;
          const widthPx = Math.min(7 * dayPx, total * dayPx - left);
          return (
            <div
              key={i}
              className="shrink-0 border-r border-gray-100 px-2 py-2 overflow-hidden"
              style={{ width: widthPx }}
            >
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                {format(weekStart, 'MMM d')}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  // Month view
  const months = eachMonthOfInterval({ start: startDate, end: endDate });
  return (
    <div
      className="flex sticky top-0 z-10 bg-white border-b border-gray-100"
      style={{ width: LABEL_W + total * dayPx }}
    >
      <div className="shrink-0 border-r border-gray-100" style={{ width: LABEL_W }} />
      {months.map((monthStart, i) => {
        const offsetDays = differenceInDays(monthStart, startDate);
        const nextMonth = addMonths(monthStart, 1);
        const daysInSlice = differenceInDays(
          nextMonth > endDate ? endDate : nextMonth,
          monthStart < startDate ? startDate : monthStart,
        );
        const left = Math.max(0, offsetDays * dayPx);
        if (left >= total * dayPx) return null;
        const widthPx = Math.min(daysInSlice * dayPx, total * dayPx - left);
        return (
          <div
            key={i}
            className="shrink-0 border-r border-gray-100 px-2 py-2 overflow-hidden"
            style={{ width: widthPx }}
          >
            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
              {format(monthStart, 'MMM yyyy')}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Today line ────────────────────────────────────────────────────────────────

function TodayLine({ startDate, total, dayPx }: { startDate: Date; total: number; dayPx: number }) {
  const offset = differenceInDays(new Date(), startDate);
  if (offset < 0 || offset > total) return null;
  return (
    <div
      className="absolute top-0 bottom-0 w-px bg-blue-400 opacity-50 z-20 pointer-events-none"
      style={{ left: LABEL_W + offset * dayPx }}
    />
  );
}

// ── Empty row ─────────────────────────────────────────────────────────────────

function EmptyRow({ total, dayPx }: { total: number; dayPx: number }) {
  return (
    <div
      className="flex items-center h-10"
      style={{ width: LABEL_W + total * dayPx }}
    >
      <div className="shrink-0 pl-4" style={{ width: LABEL_W }}>
        <span className="text-[10px] text-gray-400">No dated tasks</span>
      </div>
    </div>
  );
}

// ── Gantt Row ─────────────────────────────────────────────────────────────────

interface GanttRowProps {
  task: Task;
  startDate: Date;
  total: number;
  dayPx: number;
  color: string;
  isSelected: boolean;
  isDragging: boolean;
  dragDeltaDays: number;
  isUpdating: boolean;
  onClick: () => void;
  onBarMouseDown: (e: React.MouseEvent) => void;
}

function GanttRow({
  task,
  startDate,
  total,
  dayPx,
  color,
  isSelected,
  isDragging,
  dragDeltaDays,
  isUpdating,
  onClick,
  onBarMouseDown,
}: GanttRowProps) {
  const rawStart = task.start_date ? parseISO(task.start_date) : null;
  const rawEnd = task.end_date ? parseISO(task.end_date) : null;
  if (!rawStart || !isValid(rawStart)) return null;

  const barEnd = rawEnd && isValid(rawEnd) ? rawEnd : addDays(rawStart, 3);

  // Apply drag delta
  const effectiveStart = addDays(rawStart, dragDeltaDays);
  const effectiveEnd = addDays(barEnd, dragDeltaDays);

  const offsetDays = differenceInDays(effectiveStart, startDate);
  const durationDays = Math.max(1, differenceInDays(effectiveEnd, effectiveStart) + 1);
  const barLeft = offsetDays * dayPx;
  const barWidth = durationDays * dayPx;

  const progress = (task.metadata as any)?.progress ?? 0;

  // Don't render if fully outside window
  if (barLeft + barWidth < 0 || barLeft > total * dayPx) {
    return (
      <div className="flex items-center h-9" style={{ width: LABEL_W + total * dayPx }}>
        <div className="shrink-0 pl-4 pr-2" style={{ width: LABEL_W }}>
          <span className="text-[10px] text-gray-400 truncate block">{task.title}</span>
        </div>
      </div>
    );
  }

  const clippedLeft = Math.max(0, barLeft);
  const clippedWidth = Math.min(barWidth - (clippedLeft - barLeft), total * dayPx - clippedLeft);

  return (
    <div
      className={`flex items-center h-9 relative transition-colors ${
        isSelected ? 'bg-gray-50' : 'hover:bg-gray-50/60'
      } ${isDragging ? 'cursor-grabbing' : ''}`}
      style={{ width: LABEL_W + total * dayPx }}
    >
      {/* Label */}
      <div
        className="shrink-0 pl-4 pr-2 cursor-pointer"
        style={{ width: LABEL_W }}
        onClick={onClick}
      >
        <span className="text-[10px] text-gray-700 truncate block leading-tight font-medium">
          {task.title}
        </span>
        {task.agent_name && (
          <span className="text-[9px] text-gray-400 truncate block">{task.agent_name}</span>
        )}
      </div>

      {/* Bar */}
      <div
        role="button"
        tabIndex={0}
        className={`absolute h-5 rounded flex items-center overflow-hidden cursor-grab active:cursor-grabbing transition-shadow ${
          isSelected ? 'ring-2 ring-gray-700 ring-offset-0' : ''
        } ${isUpdating ? 'opacity-60' : ''} ${isDragging ? 'shadow-lg scale-y-105 transition-transform' : ''}`}
        style={{
          left: LABEL_W + clippedLeft,
          width: Math.max(clippedWidth, 4),
          backgroundColor: color,
        }}
        onMouseDown={onBarMouseDown}
        onClick={onClick}
        onKeyDown={(e) => e.key === 'Enter' && onClick()}
      >
        {/* Progress overlay */}
        {progress > 0 && (
          <div
            className="absolute top-0 left-0 h-full opacity-30 bg-black rounded-l"
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        )}
        <span className="relative px-1.5 text-[9px] font-semibold text-white truncate z-10">
          {task.title}
        </span>
      </div>
    </div>
  );
}

// ── Task Detail Drawer ────────────────────────────────────────────────────────

function TaskDetail({ task, onClose }: { task: Task; onClose: () => void }) {
  const office = getOffice(task.office_id);
  const progress = (task.metadata as any)?.progress ?? 0;

  return (
    <div
      className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-xl z-30"
      data-testid="gantt-task-detail"
    >
      <div className="flex items-start justify-between max-w-2xl">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {office && (
              <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: office.color }} />
            )}
            <span className="text-sm font-semibold text-gray-900 truncate">{task.title}</span>
            <span
              className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize shrink-0"
            >
              {task.column}
            </span>
          </div>

          {task.description && (
            <p className="text-xs text-gray-500 mb-2 line-clamp-2">{task.description}</p>
          )}

          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
            {task.start_date && <span>Start: <strong className="text-gray-700">{task.start_date}</strong></span>}
            {task.end_date && <span>End: <strong className="text-gray-700">{task.end_date}</strong></span>}
            {task.agent_name && <span>Agent: <strong className="text-gray-700">{task.agent_name}</strong></span>}
            {progress > 0 && <span>Progress: <strong className="text-gray-700">{progress}%</strong></span>}
          </div>
        </div>

        <button
          onClick={onClose}
          className="ml-4 text-gray-400 hover:text-gray-600 transition-colors shrink-0"
          data-testid="gantt-detail-close"
          aria-label="Close detail"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
